// src/app/shared/components/filter-panel/filter-panel.component.ts
import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component,
  EventEmitter, Input, OnChanges, Output, SimpleChanges
} from '@angular/core';
import { FilterFieldDef, FilterOperator, FilterRule, FieldType } from 'src/app/shared/utils/field-filters.types';
import { applyFieldFilters } from 'src/app/shared/utils/field-filters.util';

@Component({
  selector: 'app-filter-panel',
  templateUrl: './filter-panel.component.html',
  styleUrls: ['./filter-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilterPanelComponent implements OnChanges {
  @Input() defs: FilterFieldDef[] = [];
  @Input() rows: any[] = [];
  @Input() open = false;
  /** Previously-applied rules — pre-fill the panel when it re-opens so selections persist. */
  @Input() appliedRules: FilterRule[] = [];
  @Output() rulesApplied = new EventEmitter<FilterRule[]>();
  @Output() closed = new EventEmitter<void>();

  /** Optional server-side "Is Mandatory" control rendered at the top of the panel. */
  @Input() showMandatory = false;
  @Input() mandatoryValue: 'true' | 'false' | '' = 'true';
  /** Emits when the user changes Is Mandatory — the report re-queries (it's a server param). */
  @Output() mandatoryChange = new EventEmitter<'true' | 'false' | ''>();

  fieldSearch = '';
  workingRules: FilterRule[] = [];
  activeKeys = new Set<string>();
  enumSearchMap: Record<string, string> = {};
  /** Cached groupedDefs — rebuilt only when defs or fieldSearch changes to avoid new object refs every CD tick. */
  cachedGroupedDefs: { group: string; defs: FilterFieldDef[] }[] = [];

  constructor(private cdr: ChangeDetectorRef) {}

  onMandatoryPick(value: string): void {
    const v: 'true' | 'false' | '' = (value === 'true' || value === 'false') ? value : '';
    this.mandatoryChange.emit(v);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open'] && this.open) {
      // Restore previously-applied rules so selections persist across open/close cycles.
      this.workingRules = (this.appliedRules ?? []).map(r => ({ ...r }));
      this.activeKeys = new Set((this.appliedRules ?? []).map(r => r.key));
      this.fieldSearch = '';
      this.enumSearchMap = {};
      // Auto-populate enum options that are empty from the rows dataset.
      // This covers reports that don't explicitly populate filterDefs.options after fetch.
      this.autoPopulateEnumOptions();
      this.rebuildGroupedDefs();
      this.cdr.markForCheck();
    }
    // Re-populate if rows arrive after the panel was already open (late data load).
    if (changes['rows'] && this.open && this.rows.length > 0) {
      this.autoPopulateEnumOptions();
      this.cdr.markForCheck();
    }
    // Rebuild groupedDefs if defs input changes.
    if (changes['defs']) {
      this.rebuildGroupedDefs();
    }
  }

  private autoPopulateEnumOptions(): void {
    for (const def of this.defs) {
      if (def.type !== 'enum' || (def.options?.length ?? 0) > 0) continue;
      const accessor = def.accessor ?? ((r: any) => r[def.key]);
      const valueSet = new Set<string>();
      for (const row of this.rows) {
        const v = accessor(row);
        if (v != null && v !== '') valueSet.add(String(v));
      }
      def.options = Array.from(valueSet).sort().map(v => ({ value: v, label: v }));
    }
  }

  get filteredDefs(): FilterFieldDef[] {
    const q = this.fieldSearch.trim().toLowerCase();
    return q ? this.defs.filter(d => d.label.toLowerCase().includes(q)) : this.defs;
  }

  /** Returns the stable cached copy — rebuilt only on defs/fieldSearch changes, not on every CD tick. */
  get groupedDefs(): { group: string; defs: FilterFieldDef[] }[] {
    return this.cachedGroupedDefs;
  }

  private rebuildGroupedDefs(): void {
    const map = new Map<string, FilterFieldDef[]>();
    for (const d of this.filteredDefs) {
      const g = d.group ?? '';
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(d);
    }
    this.cachedGroupedDefs = Array.from(map.entries()).map(([group, defs]) => ({ group, defs }));
  }

  get matchCount(): number {
    return applyFieldFilters(this.rows, this.workingRules).length;
  }

  isActive(key: string): boolean { return this.activeKeys.has(key); }

  toggleField(def: FilterFieldDef): void {
    if (this.activeKeys.has(def.key)) {
      this.activeKeys.delete(def.key);
      this.workingRules = this.workingRules.filter(r => r.key !== def.key);
    } else {
      this.activeKeys.add(def.key);
      const operator = this.defaultOperator(def.type);
      const value    = def.type === 'enum' ? [] : undefined;
      this.workingRules = [...this.workingRules, { key: def.key, operator, value }];
    }
    this.cdr.markForCheck();
  }

  getRuleFor(key: string): FilterRule | undefined {
    return this.workingRules.find(r => r.key === key);
  }

  setOperator(key: string, operator: FilterOperator): void {
    this.workingRules = this.workingRules.map(r =>
      r.key === key ? { ...r, operator, value: r.key === key && this.getType(key) === 'enum' ? [] : undefined, value2: undefined } : r
    );
    this.cdr.markForCheck();
  }

  setValue(key: string, value: any): void {
    this.workingRules = this.workingRules.map(r => r.key === key ? { ...r, value } : r);
    this.cdr.markForCheck();
  }

  setValue2(key: string, value2: any): void {
    this.workingRules = this.workingRules.map(r => r.key === key ? { ...r, value2 } : r);
    this.cdr.markForCheck();
  }

  toggleEnumValue(key: string, val: any): void {
    const rule = this.getRuleFor(key);
    if (!rule) return;
    const current: any[] = Array.isArray(rule.value) ? rule.value : [];
    const next = current.includes(val) ? current.filter(v => v !== val) : [...current, val];
    this.setValue(key, next);
  }

  isEnumSelected(key: string, val: any): boolean {
    const rule = this.getRuleFor(key);
    return Array.isArray(rule?.value) && rule!.value.includes(val);
  }

  filteredOptions(key: string): { value: any; label: string }[] {
    const def = this.defs.find(d => d.key === key);
    const opts = def?.options ?? [];
    const search = (this.enumSearchMap[key] ?? '').toLowerCase();
    return search ? opts.filter(o => o.label.toLowerCase().includes(search)) : opts;
  }

  /**
   * Operator lists are cached per field type. Returning a fresh array literal on every call made
   * the options *ngFor (no trackBy) tear down and recreate every <option> on each CD pass; the
   * browser then reset the <select> to its first option while [value] never re-applied (the bound
   * operator hadn't changed) — so any picked operator visually snapped back to the first entry.
   * Same object-identity class of bug as the cachedGroupedDefs fix.
   */
  private static readonly operatorCache = new Map<FieldType, { value: FilterOperator; label: string }[]>();

  operatorsFor(type: FieldType): { value: FilterOperator; label: string }[] {
    let cached = FilterPanelComponent.operatorCache.get(type);
    if (!cached) {
      cached = this.buildOperatorsFor(type);
      FilterPanelComponent.operatorCache.set(type, cached);
    }
    return cached;
  }

  trackByOperator(_: number, op: { value: FilterOperator }): string { return op.value; }

  private buildOperatorsFor(type: FieldType): { value: FilterOperator; label: string }[] {
    switch (type) {
      case 'text': return [
        { value: 'contains',     label: 'Contains' },
        { value: 'not_contains', label: 'Does not contain' },
        { value: 'equals',       label: 'Equals' },
        { value: 'not_equals',   label: 'Does not equal' },
        { value: 'starts_with',  label: 'Starts with' },
        { value: 'is_empty',     label: 'Is empty' },
        { value: 'is_not_empty', label: 'Is not empty' },
      ];
      case 'number': return [
        { value: 'eq',           label: '= (equals)' },
        { value: 'neq',          label: '≠ (not equals)' },
        { value: 'gt',           label: '> (greater than)' },
        { value: 'gte',          label: '≥ (greater or equal)' },
        { value: 'lt',           label: '< (less than)' },
        { value: 'lte',          label: '≤ (less or equal)' },
        { value: 'between',      label: 'Between' },
        { value: 'is_empty',     label: 'Is empty' },
        { value: 'is_not_empty', label: 'Is not empty' },
      ];
      case 'date': return [
        { value: 'before',       label: 'Before' },
        { value: 'after',        label: 'After' },
        { value: 'between',      label: 'Between' },
        { value: 'today',        label: 'Today' },
        { value: 'this_week',    label: 'This week' },
        { value: 'this_month',   label: 'This month' },
        { value: 'is_empty',     label: 'Is empty' },
        { value: 'is_not_empty', label: 'Is not empty' },
      ];
      case 'enum': return [
        { value: 'is_one_of',     label: 'Is one of' },
        { value: 'is_not_one_of', label: 'Is not one of' },
      ];
      case 'boolean': return [
        { value: 'is_true',  label: 'Is Yes' },
        { value: 'is_false', label: 'Is No' },
      ];
      default: return [];
    }
  }

  needsValueInput(operator: FilterOperator): boolean {
    return !['is_empty', 'is_not_empty', 'today', 'this_week', 'this_month',
             'is_true', 'is_false'].includes(operator);
  }

  isBetween(operator: FilterOperator): boolean { return operator === 'between'; }
  isDateShortcut(operator: FilterOperator): boolean {
    return ['today', 'this_week', 'this_month'].includes(operator);
  }

  apply(): void {
    this.rulesApplied.emit([...this.workingRules]);
    this.closed.emit();
  }

  clearAll(): void {
    this.workingRules = [];
    this.activeKeys = new Set();
    this.enumSearchMap = {};
    this.fieldSearch = '';
    this.rebuildGroupedDefs();
    this.cdr.markForCheck();
  }

  close(): void { this.closed.emit(); }

  trackByKey(_: number, def: FilterFieldDef): string { return def.key; }
  trackByGroup(_: number, g: { group: string; defs: FilterFieldDef[] }): string { return g.group; }

  onFieldSearchChange(): void {
    this.rebuildGroupedDefs();
    this.cdr.markForCheck();
  }

  private defaultOperator(type: FieldType): FilterOperator {
    switch (type) {
      case 'text':    return 'contains';
      case 'number':  return 'eq';
      case 'date':    return 'before';
      case 'enum':    return 'is_one_of';
      case 'boolean': return 'is_true';
    }
  }

  private getType(key: string): FieldType | undefined {
    return this.defs.find(d => d.key === key)?.type;
  }
}
